import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Blogs.css';

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/blogs`);
      const data = await response.json();
      setBlogs(data.filter((blog) => blog.status === 'published'));
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(blogs.map((blog) => blog.category).filter(Boolean))];

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch =
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className='blogs'>
        <div className='blogs-container'>
          <div className='responsive-loading'>
            <div className='responsive-spinner' />
            <p>Loading Blogs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='blogs'>
      {/* Main Content */}
      <section className='blogs-main'>
        <div className='blogs-container'>
          {/* Search and Filter */}
          <div className='blogs-controls'>
            <div className='search-box'>
              <input
                type='text'
                placeholder='Search blogs...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className='search-input'
              />
              <i className='search-icon'>🔍</i>
            </div>

            <div className='category-filter'>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className='category-select'
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className='blogs-results-info'>
            <p>
              Showing {currentBlogs.length} of {filteredBlogs.length} blogs
            </p>
          </div>

          {/* Blogs Grid */}
          {currentBlogs.length > 0 ? (
            <div className='blogs-grid'>
              {currentBlogs.map((blog) => (
                <article key={blog.id} className='blog-card'>
                  <div className='blog-image'>
                    {blog.featured_image ? (
                      <img
                        src={
                          blog.featured_image.startsWith('http')
                            ? blog.featured_image
                            : `${process.env.REACT_APP_API_URL_WITH_OUT_API}${blog.featured_image}`
                        }
                        alt={blog.title || 'Blog image'}
                        crossOrigin='anonymous'
                      />
                    ) : null}
                    {blog.category && <span className='blog-category'>{blog.category}</span>}
                  </div>

                  <div className='blog-content'>
                    <h3 className='blog-title'>
                      <Link to={`/blog/${blog.id}`}>{blog.title}</Link>
                    </h3>

                    <div className='blog-footer'>
                      <Link to={`/blog/${blog.id}`} className='read-more-btn'>
                        Read More
                        <i className='arrow-icon'>→</i>
                      </Link>

                      {blog.tags && blog.tags.length > 0 && (
                        <div className='blog-tags'>
                          {blog.tags.slice(0, 2).map((tag, index) => (
                            <span key={index} className='blog-tag'>
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className='no-blogs'>
              <div className='no-blogs-icon'>📝</div>
              <h3>No blogs found</h3>
              <p>Try adjusting your search criteria or check back later for new content.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className='blogs-pagination'>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className='pagination-btn prev-btn'
              >
                <i className='prev-icon'>←</i>
                Previous
              </button>

              <div className='pagination-numbers'>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className='pagination-btn next-btn'
              >
                Next
                <i className='next-icon'>→</i>
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
